/**
 * Phone number input enhancement.
 *
 * Activates on every `input[type="tel"]` element on the page:
 *
 * - **keydown**: blocks any key that is not a digit or a recognised safe
 *   control key (Backspace, Delete, Tab, Home, End, arrow keys, Ctrl/Cmd
 *   combined with A / C / V / X) so that non-digit characters can never be
 *   entered.
 * - **input**: strips all non-digit characters, clamps to 10 digits, then
 *   rewrites the visible value in `(XXX) XXX-XXXX` format.
 * - **init**: formats any pre-populated value (e.g. raw `5551234567` loaded
 *   from the database or a round-trip after a validation failure) so the
 *   field always shows the formatted representation.
 *
 * On form submission the `[NormalizePhone]` model binder on the server side
 * strips all non-digit characters back to raw digits before persisting, so
 * the formatted display value is transparent to the back end.
 */

/** Safe control keys that must never be blocked in the keydown handler. */
const ALLOWED_KEYS = new Set([
    'Backspace', 'Delete', 'Tab', 'Escape',
    'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Enter',
    // Standalone modifier strokes — they never produce a character and must
    // never be prevented (e.g. Shift alone before Shift+Tab reverse-focus).
    'Shift', 'Control', 'Alt', 'Meta', 'CapsLock',
]);

/**
 * Returns true when the keyboard event represents a digit-only key press
 * or a recognised safe control action (navigation, clipboard, select-all).
 */
function isAllowedKey(e: KeyboardEvent): boolean {
    if (ALLOWED_KEYS.has(e.key)) return true;

    // Allow Ctrl+A / Ctrl+C / Ctrl+V / Ctrl+X / Ctrl+Z / Ctrl+Y
    // (and their Cmd equivalents on macOS).
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x', 'z', 'y'].includes(e.key.toLowerCase())) {
        return true;
    }

    // Allow Shift+<navigation key> for text selection (e.g. Shift+Arrow, Shift+Home).
    if (e.shiftKey && ALLOWED_KEYS.has(e.key)) return true;

    // Allow digit keys (top row and numpad).
    if (/^\d$/.test(e.key)) return true;

    return false;
}

/**
 * Formats a string of up to 10 raw digits into the `(XXX) XXX-XXXX` pattern:
 *
 * | Digits | Display        |
 * |--------|----------------|
 * | 0      | ``             |
 * | 1–3    | `XXX`          |
 * | 4–6    | `(XXX) XXX`    |
 * | 7–10   | `(XXX) XXX-XXXX` |
 *
 * @param digits - A string containing only digit characters (0–9).
 * @returns The formatted phone string.
 */
export function formatPhone(digits: string): string {
    const d = digits.replace(/\D/g, '').slice(0, 10);

    if (d.length === 0) return '';
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;

    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** Strips all non-digit characters from the given string. */
function digitsOnly(value: string): string {
    return value.replace(/\D/g, '');
}

function handleKeyDown(e: KeyboardEvent): void {
    if (!isAllowedKey(e)) {
        e.preventDefault();
    }
}

function handleInput(this: HTMLInputElement): void {
    const raw = digitsOnly(this.value);
    this.value = formatPhone(raw);
}

/**
 * Attaches phone-input behaviour to every `input[type="tel"]` found within
 * `root` (defaults to `document`).
 *
 * Existing values are formatted immediately on call so pre-populated fields
 * (round-trips after validation failures, or raw digits loaded from the DB)
 * always display in the `(XXX) XXX-XXXX` style.
 *
 * @param root - The root element or document to search. Defaults to `document`.
 */
export function initPhoneInputs(root: Document | Element = document): void {
    const inputs = root.querySelectorAll<HTMLInputElement>('input[type="tel"]');

    inputs.forEach(input => {
        // Format any pre-populated value immediately.
        if (input.value) {
            input.value = formatPhone(digitsOnly(input.value));
        }

        input.addEventListener('keydown', handleKeyDown);
        input.addEventListener('input', handleInput);
    });
}
